import { useState, useEffect, useMemo, type ReactNode } from "react";
import { Card } from "../Card";
import { Avatar } from "../Avatar";
import { apiGet } from "../../lib/api";
import type { AvatarId, DisciplineSlug } from "@jumpbean/shared";

/**
 * Types for the ability API response
 */
interface SkillAbilitySummary {
  skillKey: string;
  displayName: string;
  theta: number;
  abilityLevel: "beginner" | "developing" | "strong" | "expert";
  confidence: number;
  sampleSize: number;
  needsMorePractice: boolean;
}

interface DisciplineAbilitySummary {
  disciplineId: string;
  disciplineSlug: DisciplineSlug;
  disciplineName: string;
  averageTheta: number;
  abilityLevel: "beginner" | "developing" | "strong" | "expert";
  averageConfidence: number;
  totalSamples: number;
  skills: SkillAbilitySummary[];
  weakestSkills: SkillAbilitySummary[];
  strongestSkills: SkillAbilitySummary[];
}

interface AbilitySummary {
  childId: string;
  childName: string;
  lastUpdated: string;
  disciplines: DisciplineAbilitySummary[];
  overallAbilityLevel: "beginner" | "developing" | "strong" | "expert";
  overallTheta: number;
  tips: string[];
}

export interface AbilityDashboardProps {
  /** Child whose abilities to display */
  child: {
    id: string;
    displayName: string;
    avatarId: string;
  };
  /** Whether to show compact view */
  compact?: boolean;
  /** Callback when "View Full Report" is clicked */
  onViewFullReport?: () => void;
}

// Discipline colors for consistent theming
const DISCIPLINE_COLORS: Record<DisciplineSlug, { bg: string; text: string; ring: string; gradient: string }> = {
  math: {
    bg: "bg-luna-100",
    text: "text-luna",
    ring: "ring-luna",
    gradient: "from-luna-light to-luna",
  },
  spelling: {
    bg: "bg-petal-100",
    text: "text-petal",
    ring: "ring-petal",
    gradient: "from-petal-light to-petal",
  },
  patterns: {
    bg: "bg-prism-yellow/20",
    text: "text-prism-red",
    ring: "ring-prism-red",
    gradient: "from-prism-red to-prism-yellow",
  },
  typing: {
    bg: "bg-whiskers-light/30",
    text: "text-whiskers",
    ring: "ring-whiskers",
    gradient: "from-whiskers-light to-whiskers",
  },
  programming: {
    bg: "bg-pixel-light/30",
    text: "text-pixel",
    ring: "ring-pixel",
    gradient: "from-pixel-light to-pixel",
  },
};

// Ability level configuration
const ABILITY_LEVELS = {
  beginner: { label: "Beginner", emoji: "🌱", color: "text-sky-600", bg: "bg-sky-100" },
  developing: { label: "Developing", emoji: "🌿", color: "text-meadow-600", bg: "bg-meadow-100" },
  strong: { label: "Strong", emoji: "🌳", color: "text-sunshine-600", bg: "bg-sunshine-100" },
  expert: { label: "Expert", emoji: "⭐", color: "text-magic-600", bg: "bg-magic-100" },
};

/**
 * AbilityDashboard - Visual display of a child's learning abilities
 *
 * Shows ability estimates per discipline and skill with parent-friendly labels,
 * progress indicators, and actionable tips based on the adaptive difficulty system.
 *
 * @example
 * <AbilityDashboard
 *   child={{ id: "uuid", displayName: "Emma", avatarId: "luna" }}
 * />
 */
export function AbilityDashboard({
  child,
  compact = false,
  onViewFullReport,
}: AbilityDashboardProps) {
  const [abilities, setAbilities] = useState<AbilitySummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDiscipline, setExpandedDiscipline] = useState<string | null>(null);

  // Fetch abilities on mount and when child changes
  useEffect(() => {
    fetchAbilities();
  }, [child.id]);

  async function fetchAbilities() {
    setIsLoading(true);
    setError(null);

    const { data, error: fetchError } = await apiGet<AbilitySummary>(
      `/api/adaptive/ability/${child.id}`
    );

    if (fetchError) {
      setError(fetchError.message || "Failed to load abilities");
    } else if (data) {
      setAbilities(data);
    }

    setIsLoading(false);
  }

  // Format last updated time
  const lastUpdatedText = useMemo(() => {
    if (!abilities?.lastUpdated) return "";

    const date = new Date(abilities.lastUpdated);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffHours < 1) return "Updated just now";
    if (diffHours < 24) return `Updated ${diffHours}h ago`;
    if (diffDays === 1) return "Updated yesterday";
    if (diffDays < 7) return `Updated ${diffDays} days ago`;

    return `Updated ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  }, [abilities?.lastUpdated]);

  // Calculate overall progress percentage (normalize theta from -3..3 to 0..100)
  const overallProgress = useMemo(() => {
    if (!abilities) return 0;
    // theta ranges from -3 to +3, map to 0-100
    return Math.round(((abilities.overallTheta + 3) / 6) * 100);
  }, [abilities?.overallTheta]);

  // Toggle discipline expansion
  const toggleDiscipline = (disciplineId: string) => {
    setExpandedDiscipline(expandedDiscipline === disciplineId ? null : disciplineId);
  };

  // Loading state
  if (isLoading) {
    return (
      <Card variant="elevated" className="p-6">
        <div className="flex items-center justify-center py-8">
          <div className="inline-block w-8 h-8 border-4 border-magic-300 border-t-magic-500 rounded-full animate-spin" />
          <p className="font-body text-body-md text-neutral-500 ml-3">
            Loading abilities...
          </p>
        </div>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card variant="elevated" className="p-6">
        <div className="text-center py-6">
          <span className="text-4xl block mb-3">😢</span>
          <p className="font-body text-body-md text-neutral-600 mb-4">{error}</p>
          <button
            onClick={fetchAbilities}
            className="font-body font-medium text-magic-500 hover:text-magic-600 transition-colors"
          >
            Try again
          </button>
        </div>
      </Card>
    );
  }

  // Empty state - no ability data yet
  if (!abilities || abilities.disciplines.length === 0) {
    return (
      <Card variant="elevated" className="p-6">
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-sunshine-100 to-magic-100 flex items-center justify-center">
            <span className="text-3xl">🌱</span>
          </div>
          <h3 className="font-display font-bold text-display-sm text-neutral-800 mb-2">
            Starting Fresh!
          </h3>
          <p className="font-body text-body-md text-neutral-600 max-w-sm mx-auto">
            {child.displayName}'s abilities will appear here as they complete challenges.
            Enable Smart Mode in settings to unlock personalized learning!
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card variant="elevated" className="overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-neutral-100 bg-gradient-to-r from-magic-50/50 to-fairy-50/50">
        <div className="flex items-center gap-4">
          <Avatar avatarId={child.avatarId as AvatarId} size="lg" />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-display font-bold text-display-sm text-neutral-800">
                {abilities.childName}'s Abilities
              </h3>
              <span
                className={`
                  px-2 py-0.5 rounded-full text-xs font-display font-semibold
                  ${ABILITY_LEVELS[abilities.overallAbilityLevel].bg}
                  ${ABILITY_LEVELS[abilities.overallAbilityLevel].color}
                `}
              >
                {ABILITY_LEVELS[abilities.overallAbilityLevel].emoji}{" "}
                {ABILITY_LEVELS[abilities.overallAbilityLevel].label}
              </span>
            </div>
            <p className="font-body text-body-sm text-neutral-500">{lastUpdatedText}</p>
          </div>
        </div>

        {/* Overall progress ring */}
        <div className="mt-4 flex items-center gap-4">
          <div className="relative w-16 h-16">
            <svg className="w-full h-full transform -rotate-90">
              {/* Background circle */}
              <circle
                cx="32"
                cy="32"
                r="28"
                fill="none"
                stroke="currentColor"
                strokeWidth="6"
                className="text-neutral-200"
              />
              {/* Progress circle */}
              <circle
                cx="32"
                cy="32"
                r="28"
                fill="none"
                stroke="url(#progressGradient)"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${overallProgress * 1.76} 176`}
                className="transition-all duration-700 ease-out"
              />
              <defs>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#a855f7" />
                  <stop offset="100%" stopColor="#ec4899" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-display font-bold text-body-lg text-neutral-800">
                {Math.round(overallProgress)}%
              </span>
            </div>
          </div>
          <div>
            <p className="font-body font-semibold text-body-md text-neutral-800">
              Overall Progress
            </p>
            <p className="font-body text-body-sm text-neutral-500">
              Across {abilities.disciplines.length} subject{abilities.disciplines.length > 1 ? "s" : ""}
            </p>
          </div>
        </div>
      </div>

      {/* Discipline breakdown */}
      <div className="p-5">
        <h4 className="font-display font-semibold text-body-md text-neutral-700 mb-4">
          By Subject
        </h4>

        <div className="space-y-3">
          {abilities.disciplines.map((discipline) => (
            <DisciplineCard
              key={discipline.disciplineId}
              discipline={discipline}
              isExpanded={expandedDiscipline === discipline.disciplineId}
              onToggle={() => toggleDiscipline(discipline.disciplineId)}
              compact={compact}
            />
          ))}
        </div>
      </div>

      {/* Tips Section */}
      {abilities.tips.length > 0 && (
        <div className="px-5 pb-5">
          <div className="p-4 bg-gradient-to-br from-sunshine-50 to-fairy-50 rounded-xl border border-sunshine-200">
            <div className="flex items-start gap-3">
              <span className="text-2xl flex-shrink-0">💡</span>
              <div>
                <p className="font-display font-semibold text-body-md text-neutral-800 mb-2">
                  Suggestions for {child.displayName}
                </p>
                <ul className="space-y-1.5">
                  {abilities.tips.map((tip, index) => (
                    <li key={index} className="font-body text-body-sm text-neutral-600 flex items-start gap-2">
                      <span className="text-meadow-500 flex-shrink-0">•</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Full Report button (if callback provided) */}
      {onViewFullReport && !compact && (
        <div className="px-5 pb-5">
          <button
            onClick={onViewFullReport}
            className="
              w-full flex items-center justify-center gap-2 px-4 py-3
              bg-gradient-to-r from-magic-500 to-magic-600
              hover:from-magic-600 hover:to-magic-700
              text-cloud rounded-xl font-body font-medium text-sm
              transition-all shadow-sm hover:shadow-md
            "
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            View Full Progress Report
          </button>
        </div>
      )}
    </Card>
  );
}

/**
 * DisciplineCard - Individual discipline ability display
 */
interface DisciplineCardProps {
  discipline: DisciplineAbilitySummary;
  isExpanded: boolean;
  onToggle: () => void;
  compact?: boolean;
}

function DisciplineCard({ discipline, isExpanded, onToggle, compact }: DisciplineCardProps) {
  const colors = DISCIPLINE_COLORS[discipline.disciplineSlug] || DISCIPLINE_COLORS.math;
  const level = ABILITY_LEVELS[discipline.abilityLevel];

  // Calculate discipline progress (normalize theta from -3..3 to 0..100)
  const progress = Math.round(((discipline.averageTheta + 3) / 6) * 100);

  // Confidence label
  const confidenceLabel = discipline.averageConfidence < 0.5
    ? "More practice needed"
    : "Well understood";

  return (
    <div
      className={`
        rounded-xl border-2 transition-all
        ${isExpanded ? `border-neutral-300 shadow-sm` : "border-neutral-100"}
        ${colors.bg}
      `}
    >
      {/* Header - clickable to expand */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-4 text-left"
        aria-expanded={isExpanded}
      >
        {/* Discipline icon */}
        <div
          className={`
            w-12 h-12 rounded-xl flex items-center justify-center
            bg-gradient-to-br ${colors.gradient} text-cloud shadow-sm
          `}
        >
          <DisciplineIcon slug={discipline.disciplineSlug} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h5 className="font-display font-bold text-body-lg text-neutral-800">
              {discipline.disciplineName}
            </h5>
            <span
              className={`
                px-1.5 py-0.5 rounded text-xs font-body font-medium
                ${level.bg} ${level.color}
              `}
            >
              {level.label}
            </span>
          </div>

          {/* Progress bar */}
          <div className="h-2 bg-neutral-200/50 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full bg-gradient-to-r ${colors.gradient} transition-all duration-500`}
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Confidence */}
          <p className="font-body text-body-xs text-neutral-500 mt-1">
            {confidenceLabel} • {discipline.totalSamples} challenge{discipline.totalSamples !== 1 ? "s" : ""} completed
          </p>
        </div>

        {/* Expand/collapse icon */}
        <svg
          className={`w-5 h-5 text-neutral-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded content - skill breakdown */}
      {isExpanded && !compact && (
        <div className="px-4 pb-4 border-t border-neutral-200/50">
          {/* Weakest skills */}
          {discipline.weakestSkills.length > 0 && (
            <div className="mt-4">
              <h6 className="font-body font-semibold text-body-sm text-neutral-600 mb-2 flex items-center gap-2">
                <span className="text-fairy-500">↓</span> Needs Practice
              </h6>
              <div className="space-y-2">
                {discipline.weakestSkills.map((skill) => (
                  <SkillRow key={skill.skillKey} skill={skill} variant="weak" />
                ))}
              </div>
            </div>
          )}

          {/* Strongest skills */}
          {discipline.strongestSkills.length > 0 && (
            <div className="mt-4">
              <h6 className="font-body font-semibold text-body-sm text-neutral-600 mb-2 flex items-center gap-2">
                <span className="text-meadow-500">↑</span> Going Great
              </h6>
              <div className="space-y-2">
                {discipline.strongestSkills.map((skill) => (
                  <SkillRow key={skill.skillKey} skill={skill} variant="strong" />
                ))}
              </div>
            </div>
          )}

          {/* All skills (if enough) */}
          {discipline.skills.length > 6 && (
            <details className="mt-4">
              <summary className="font-body font-medium text-body-sm text-magic-500 cursor-pointer hover:text-magic-600">
                Show all {discipline.skills.length} skills
              </summary>
              <div className="mt-3 space-y-2">
                {discipline.skills
                  .sort((a, b) => b.theta - a.theta)
                  .map((skill) => (
                    <SkillRow key={skill.skillKey} skill={skill} variant="neutral" />
                  ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * SkillRow - Individual skill display
 */
interface SkillRowProps {
  skill: SkillAbilitySummary;
  variant: "weak" | "strong" | "neutral";
}

function SkillRow({ skill, variant }: SkillRowProps) {
  const level = ABILITY_LEVELS[skill.abilityLevel];
  // Normalize theta to progress percentage
  const progress = Math.round(((skill.theta + 3) / 6) * 100);

  const variantColors = {
    weak: "bg-fairy-100 border-fairy-200",
    strong: "bg-meadow-100 border-meadow-200",
    neutral: "bg-neutral-50 border-neutral-200",
  };

  return (
    <div
      className={`
        p-3 rounded-lg border ${variantColors[variant]}
        flex items-center gap-3
      `}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-body font-medium text-body-sm text-neutral-800 truncate">
            {skill.displayName}
          </span>
          <span className={`text-sm ${level.color}`}>{level.emoji}</span>
        </div>
        <div className="h-1.5 bg-neutral-200/50 rounded-full overflow-hidden">
          <div
            className={`
              h-full rounded-full transition-all duration-500
              ${variant === "weak" ? "bg-fairy-400" : variant === "strong" ? "bg-meadow-400" : "bg-neutral-400"}
            `}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      <span className="font-body text-body-xs text-neutral-400 flex-shrink-0">
        {skill.sampleSize}x
      </span>
    </div>
  );
}

/**
 * DisciplineIcon - Icon for each discipline
 */
function DisciplineIcon({ slug }: { slug: DisciplineSlug }) {
  const icons: Record<DisciplineSlug, ReactNode> = {
    math: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    spelling: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
      </svg>
    ),
    patterns: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
      </svg>
    ),
    typing: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    programming: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
  };

  return icons[slug] || icons.math;
}

export default AbilityDashboard;
